"use client";

import type { CSSProperties, FocusEvent } from "react";
import type { FunnelPlannerContent } from "@/lib/content/entries/funnel-planner";
import type { TrafficChannel } from "@/lib/funnel-planner/schema";
import { getChannelDef } from "@/lib/funnel-planner/channels";
import { acquisitionModeLabel, channelLabel, funnelText } from "@/lib/funnel-planner/i18n";
import { CustomSelect } from "@/components/funnel-planner/ui/CustomSelect";

const LABEL_S: CSSProperties = {
  fontSize: 11,
  fontWeight: 500,
  color: "#94a3b8",
  display: "block",
  marginBottom: 4,
};

const INPUT_S: CSSProperties = {
  width: "100%",
  borderRadius: 6,
  border: "1px solid #334155",
  background: "rgba(255,255,255,0.05)",
  padding: "6px 10px",
  fontSize: 13,
  color: "#e2e8f0",
  outline: "none",
  boxSizing: "border-box",
};

const ACQTYPE_FALLBACKS: Record<TrafficChannel["acquisitionType"], string> = {
  organic: "Organic",
  cpc: "CPC (paid per click)",
  cpm: "CPM (paid per impression)",
  cpv: "CPV (paid per view)",
  flat_fee: "Flat monthly fee",
  cpa: "CPA (paid per customer)",
  cost_per_lead: "CPL (paid per lead)",
  revenue_share: "Revenue share",
  labor_time: "Labor / time cost",
  organic_unknown: "Organic / unknown cost",
};

const ATTRIBUTION_ROLE_OPTIONS: Array<{ value: NonNullable<TrafficChannel["attributionRole"]>; key: string; fallback: string }> = [
  { value: "first_touch", key: "trafficFirstTouchOption", fallback: "First touch" },
  { value: "last_touch", key: "trafficLastTouchOption", fallback: "Last touch" },
  { value: "assist", key: "trafficAssistOption", fallback: "Assist" },
  { value: "retargeting", key: "trafficRetargetingOption", fallback: "Retargeting" },
  { value: "direct", key: "trafficDirectOption", fallback: "Direct" },
];

const TRACKING_SETUP_OPTIONS: Array<{ value: NonNullable<TrafficChannel["trackingSetup"]>; key: string; fallback: string }> = [
  { value: "none", key: "trafficTrackingNoneOption", fallback: "None" },
  { value: "utm_only", key: "trafficTrackingUtmOnlyOption", fallback: "UTM only" },
  { value: "pixel", key: "trafficTrackingPixelOption", fallback: "Pixel" },
  { value: "server_side", key: "trafficTrackingServerSideOption", fallback: "Server-side" },
  { value: "crm_matched", key: "trafficTrackingCrmMatchedOption", fallback: "CRM matched" },
];

const AUDIENCE_TEMPERATURE_OPTIONS: Array<{ value: NonNullable<TrafficChannel["audienceTemperature"]>; key: string; fallback: string }> = [
  { value: "cold", key: "trafficTemperatureColdOption", fallback: "Cold" },
  { value: "warm", key: "trafficTemperatureWarmOption", fallback: "Warm" },
  { value: "hot", key: "trafficTemperatureHotOption", fallback: "Hot" },
];

const DEVICE_OPTIONS: Array<{ value: NonNullable<TrafficChannel["device"]>; key: string; fallback: string }> = [
  { value: "all", key: "trafficDeviceAllOption", fallback: "All devices" },
  { value: "desktop", key: "trafficDeviceDesktopOption", fallback: "Desktop" },
  { value: "mobile", key: "trafficDeviceMobileOption", fallback: "Mobile" },
  { value: "tablet", key: "trafficDeviceTabletOption", fallback: "Tablet" },
];

const RAMP_TYPE_OPTIONS: Array<{ value: NonNullable<TrafficChannel["rampType"]>; key: string; fallback: string }> = [
  { value: "flat", key: "trafficRampFlatOption", fallback: "Flat" },
  { value: "linear", key: "trafficRampLinearOption", fallback: "Linear" },
  { value: "seasonal", key: "trafficRampSeasonalOption", fallback: "Seasonal" },
  { value: "launch_spike", key: "trafficRampLaunchSpikeOption", fallback: "Launch spike" },
  { value: "decay", key: "trafficRampDecayOption", fallback: "Decay" },
];

const CONFIDENCE_OPTIONS: Array<{ value: NonNullable<TrafficChannel["confidence"]>; key: string; fallback: string }> = [
  { value: "low", key: "trafficConfidenceLowOption", fallback: "Low" },
  { value: "medium", key: "trafficConfidenceMediumOption", fallback: "Medium" },
  { value: "high", key: "trafficConfidenceHighOption", fallback: "High" },
];

const INPUT_FOCUS_HANDLERS = {
  onFocus: (e: FocusEvent<HTMLInputElement>) => {
    e.currentTarget.style.outline = "2px solid #3b82f6";
    e.currentTarget.style.outlineOffset = "2px";
  },
  onBlur: (e: FocusEvent<HTMLInputElement>) => {
    e.currentTarget.style.outline = "none";
    e.currentTarget.style.outlineOffset = "0";
  },
};

function parseOptionalFloat(raw: string): number | undefined {
  return raw === "" ? undefined : parseFloat(raw);
}

type ChannelConfigBlockProps = {
  channel: TrafficChannel;
  content: FunnelPlannerContent;
  currencyCode: string;
  isLast: boolean;
  mode: "panel" | "inline";
  onChange: (updated: TrafficChannel) => void;
  onRemove: (id: string) => void;
  onTouched: (inputId: string) => void;
  setBlockRef?: (id: string, element: HTMLDivElement | null) => void;
  touchedInputs: Set<string>;
  id?: string;
};

export function ChannelConfigBlock({
  channel: ch,
  content,
  currencyCode,
  id,
  isLast,
  mode,
  onChange,
  onRemove,
  onTouched,
  setBlockRef,
  touchedInputs,
}: ChannelConfigBlockProps) {
  const def = getChannelDef(ch.id);
  if (!def) return null;

  const monetaryLabel = (key: string, fallback: string) =>
    funnelText(content, key, fallback).replace(/\bUSD\b/g, currencyCode);
  const acqTypeOptions = (Object.keys(ACQTYPE_FALLBACKS) as Array<TrafficChannel["acquisitionType"]>).map((value) => ({
    value,
    label: acquisitionModeLabel(content, value, ACQTYPE_FALLBACKS[value]),
  }));
  const visitorsId = `${ch.id}-visitors`;
  const cpcId = `${ch.id}-cpc`;
  const cpvId = `${ch.id}-cpv`;
  const cpmId = `${ch.id}-cpm`;
  const ctrId = `${ch.id}-ctr`;
  const flatFeeId = `${ch.id}-flat-fee`;
  const cpaId = `${ch.id}-cpa`;
  const cplId = `${ch.id}-cpl`;
  const revenueShareId = `${ch.id}-revenue-share`;
  const laborHoursId = `${ch.id}-labor-hours`;
  const hourlyCostId = `${ch.id}-hourly-cost`;
  const campaignId = `${ch.id}-campaign`;
  const visitorsInvalid = touchedInputs.has(visitorsId) && ch.monthlyVisitors === 0;

  return (
    <div
      id={id}
      ref={(el) => setBlockRef?.(ch.id, el)}
      data-funnel-planner-channel-config-block="true"
      data-funnel-planner-channel-config-block-mode={mode}
      style={{
        padding: "12px 14px",
        borderBottom: mode === "panel" && !isLast ? "1px solid #0f172a" : "none",
        border: mode === "inline" ? "1px solid #1e293b" : undefined,
        borderRadius: mode === "inline" ? 10 : undefined,
        background: mode === "inline" ? "rgba(255,255,255,0.025)" : undefined,
        marginTop: mode === "inline" ? 6 : undefined,
        marginBottom: mode === "inline" ? 8 : undefined,
        overflow: "hidden",
      }}
    >
      <div style={{
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        marginBottom: 10,
      }}>
        <div style={{ display: "flex", alignItems: "center", gap: 7, minWidth: 0 }}>
          <span
            aria-hidden="true"
            style={{
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
              minWidth: 26,
              height: 20,
              borderRadius: 4,
              background: "rgba(37,99,235,0.20)",
              fontSize: 9,
              fontWeight: 700,
              color: "#93c5fd",
              padding: "0 3px",
              letterSpacing: "-0.02em",
              flexShrink: 0,
            }}
          >
            {def.icon}
          </span>
          <span style={{
            fontSize: 12,
            fontWeight: 600,
            color: "#cbd5e1",
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }}>
            {def.label}
          </span>
        </div>
        <button
          type="button"
          onClick={() => onRemove(ch.id)}
          aria-label={funnelText(content, "trafficRemoveChannelAria", "Remove {channel}", {
            channel: channelLabel(content, ch.id, ch.label),
          })}
          style={{
            background: "transparent",
            border: "none",
            cursor: "pointer",
            color: "#94a3b8",
            fontSize: 16,
            lineHeight: 1,
            padding: "10px 12px",
            minWidth: 44,
            minHeight: 44,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            flexShrink: 0,
            borderRadius: 4,
          }}
          onMouseEnter={(e) => {
            (e.currentTarget as HTMLButtonElement).style.color = "#ef4444";
            (e.currentTarget as HTMLButtonElement).style.textDecoration = "underline";
          }}
          onMouseLeave={(e) => {
            (e.currentTarget as HTMLButtonElement).style.color = "#94a3b8";
            (e.currentTarget as HTMLButtonElement).style.textDecoration = "none";
          }}
        >
          x
        </button>
      </div>

      <div
        data-funnel-planner-channel-field-grid="true"
        style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8, marginBottom: 8 }}
      >
        <div>
          <label htmlFor={visitorsId} style={LABEL_S}>{funnelText(content, "trafficVisitorsLabel", "Visitors /mo")}</label>
          <input
            id={visitorsId}
            type="number"
            min={0}
            max={10_000_000}
            value={ch.monthlyVisitors || ""}
            placeholder={funnelText(content, "trafficVisitorsPlaceholder", "e.g. 5000")}
            onChange={(e) =>
              onChange({ ...ch, monthlyVisitors: Math.max(0, parseInt(e.target.value) || 0) })
            }
            onBlur={(e) => {
              onTouched(visitorsId);
              INPUT_FOCUS_HANDLERS.onBlur(e);
            }}
            onFocus={INPUT_FOCUS_HANDLERS.onFocus}
            style={{
              ...INPUT_S,
              border: visitorsInvalid ? "1px solid #f59e0b" : "1px solid #334155",
            }}
            aria-describedby={visitorsInvalid ? `${visitorsId}-err` : undefined}
          />
          {visitorsInvalid && (
            <p id={`${visitorsId}-err`} style={{ fontSize: 10, color: "#f59e0b", marginTop: 3 }}>
              {funnelText(content, "trafficVisitorsRequired", "Enter visitors to continue")}
            </p>
          )}
        </div>
        <div>
          <label htmlFor={`${ch.id}-type`} style={LABEL_S}>{funnelText(content, "trafficAcquisitionModelLabel", "Acquisition model")}</label>
          <CustomSelect
            id={`${ch.id}-type`}
            value={ch.acquisitionType}
            onChange={(v) => onChange({ ...ch, acquisitionType: v as TrafficChannel["acquisitionType"] })}
            options={acqTypeOptions}
          />
        </div>
      </div>

      {ch.acquisitionType === "cpc" && (
        <div style={{ marginBottom: 8 }}>
          <label htmlFor={cpcId} style={LABEL_S}>{monetaryLabel("trafficCostPerClickLabel", "Cost per click (USD)")}</label>
          <input
            id={cpcId}
            type="number"
            min={0}
            step={0.01}
            value={ch.cpc ?? ""}
            placeholder="e.g. 2.50"
            onChange={(e) => onChange({ ...ch, cpc: parseOptionalFloat(e.target.value) })}
            {...INPUT_FOCUS_HANDLERS}
            style={INPUT_S}
          />
        </div>
      )}

      {ch.acquisitionType === "cpv" && (
        <div style={{ marginBottom: 8 }}>
          <label htmlFor={cpvId} style={LABEL_S}>{monetaryLabel("trafficCostPerViewLabel", "Cost per view (USD)")}</label>
          <input
            id={cpvId}
            type="number"
            min={0}
            step={0.01}
            value={ch.costPerView ?? ""}
            placeholder="0.08"
            onChange={(e) => onChange({ ...ch, costPerView: parseOptionalFloat(e.target.value) })}
            {...INPUT_FOCUS_HANDLERS}
            style={INPUT_S}
          />
        </div>
      )}

      {ch.acquisitionType === "cpm" && (
        <div
          data-funnel-planner-channel-field-grid="true"
          style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8, marginBottom: 8 }}
        >
          <div>
            <label htmlFor={cpmId} style={LABEL_S}>{monetaryLabel("trafficCpmLabel", "CPM (USD)")}</label>
            <input
              id={cpmId}
              type="number"
              min={0}
              step={0.01}
              value={ch.cpm ?? ""}
              placeholder="5.00"
              onChange={(e) => onChange({ ...ch, cpm: parseOptionalFloat(e.target.value) })}
              {...INPUT_FOCUS_HANDLERS}
              style={INPUT_S}
            />
          </div>
          <div>
            <label htmlFor={ctrId} style={LABEL_S}>{funnelText(content, "trafficCtrLabel", "CTR (%)")}</label>
            <input
              id={ctrId}
              type="number"
              min={0}
              max={100}
              step={0.1}
              value={ch.ctr ?? ""}
              placeholder="1.5"
              onChange={(e) => {
                const v = parseOptionalFloat(e.target.value);
                onChange({ ...ch, ctr: v == null ? undefined : Math.min(100, v) });
              }}
              {...INPUT_FOCUS_HANDLERS}
              style={INPUT_S}
            />
          </div>
        </div>
      )}

      {ch.acquisitionType === "flat_fee" && (
        <div style={{ marginBottom: 8 }}>
          <label htmlFor={flatFeeId} style={LABEL_S}>{monetaryLabel("trafficFlatFeeLabel", "Flat monthly fee (USD)")}</label>
          <input
            id={flatFeeId}
            type="number"
            min={0}
            step={1}
            value={ch.flatMonthlyFee ?? ""}
            placeholder="1500"
            onChange={(e) => onChange({ ...ch, flatMonthlyFee: parseOptionalFloat(e.target.value) })}
            {...INPUT_FOCUS_HANDLERS}
            style={INPUT_S}
          />
        </div>
      )}

      {ch.acquisitionType === "cpa" && (
        <div style={{ marginBottom: 8 }}>
          <label htmlFor={cpaId} style={LABEL_S}>{monetaryLabel("trafficCostPerCustomerLabel", "Cost per customer (USD)")}</label>
          <input
            id={cpaId}
            type="number"
            min={0}
            step={1}
            value={ch.costPerAcquisition ?? ""}
            placeholder="45"
            onChange={(e) => onChange({ ...ch, costPerAcquisition: parseOptionalFloat(e.target.value) })}
            {...INPUT_FOCUS_HANDLERS}
            style={INPUT_S}
          />
        </div>
      )}

      {ch.acquisitionType === "cost_per_lead" && (
        <div style={{ marginBottom: 8 }}>
          <label htmlFor={cplId} style={LABEL_S}>{monetaryLabel("trafficCostPerLeadLabel", "Cost per lead (USD)")}</label>
          <input
            id={cplId}
            type="number"
            min={0}
            step={0.01}
            value={ch.costPerLead ?? ""}
            placeholder="12.50"
            onChange={(e) => onChange({ ...ch, costPerLead: parseOptionalFloat(e.target.value) })}
            {...INPUT_FOCUS_HANDLERS}
            style={INPUT_S}
          />
        </div>
      )}

      {ch.acquisitionType === "revenue_share" && (
        <div style={{ marginBottom: 8 }}>
          <label htmlFor={revenueShareId} style={LABEL_S}>{funnelText(content, "trafficRevenueShareLabel", "Revenue share (%)")}</label>
          <input
            id={revenueShareId}
            type="number"
            min={0}
            max={100}
            step={0.1}
            value={ch.revenueSharePercent ?? ""}
            placeholder="20"
            onChange={(e) => {
              const v = parseOptionalFloat(e.target.value);
              onChange({ ...ch, revenueSharePercent: v == null ? undefined : Math.min(100, v) });
            }}
            {...INPUT_FOCUS_HANDLERS}
            style={INPUT_S}
          />
        </div>
      )}

      {ch.acquisitionType === "labor_time" && (
        <div
          data-funnel-planner-channel-field-grid="true"
          style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8, marginBottom: 8 }}
        >
          <div>
            <label htmlFor={laborHoursId} style={LABEL_S}>{funnelText(content, "trafficLaborHoursLabel", "Hours / mo")}</label>
            <input
              id={laborHoursId}
              type="number"
              min={0}
              step={0.5}
              value={ch.laborHoursPerMonth ?? ""}
              placeholder="12"
              onChange={(e) => onChange({ ...ch, laborHoursPerMonth: parseOptionalFloat(e.target.value) })}
              {...INPUT_FOCUS_HANDLERS}
              style={INPUT_S}
            />
          </div>
          <div>
            <label htmlFor={hourlyCostId} style={LABEL_S}>{monetaryLabel("trafficHourlyCostLabel", "Hourly cost (USD)")}</label>
            <input
              id={hourlyCostId}
              type="number"
              min={0}
              step={1}
              value={ch.hourlyCost ?? ""}
              placeholder="75"
              onChange={(e) => onChange({ ...ch, hourlyCost: parseOptionalFloat(e.target.value) })}
              {...INPUT_FOCUS_HANDLERS}
              style={INPUT_S}
            />
          </div>
        </div>
      )}

      <div>
        <label htmlFor={campaignId} style={LABEL_S}>
          {funnelText(content, "trafficCampaignLabel", "Campaign")} <span style={{ color: "#64748b" }}>({funnelText(content, "optionalLabel", "optional")})</span>
        </label>
        <input
          id={campaignId}
          type="text"
          value={ch.campaignName ?? ""}
          maxLength={120}
          placeholder={funnelText(content, "trafficCampaignPlaceholder", "e.g. Q3 awareness")}
          onChange={(e) => onChange({ ...ch, campaignName: e.target.value || undefined })}
          {...INPUT_FOCUS_HANDLERS}
          style={INPUT_S}
        />
      </div>
      <details style={{ marginTop: 10 }}>
        <summary style={{ cursor: "pointer", fontSize: 11, fontWeight: 700, color: "#93c5fd", textTransform: "uppercase", letterSpacing: "0.04em" }}>
          {funnelText(content, "trafficAdvancedTrackingLabel", "Advanced tracking")}
        </summary>
        <div
          data-funnel-planner-channel-field-grid="true"
          style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8, marginTop: 10 }}
        >
          <div>
            <label htmlFor={`${ch.id}-attribution`} style={LABEL_S}>{funnelText(content, "trafficAttributionRoleLabel", "Attribution role")}</label>
            <CustomSelect
              id={`${ch.id}-attribution`}
              value={ch.attributionRole ?? "first_touch"}
              onChange={(v) => onChange({ ...ch, attributionRole: v as TrafficChannel["attributionRole"] })}
              options={ATTRIBUTION_ROLE_OPTIONS.map((option) => ({ value: option.value, label: funnelText(content, option.key, option.fallback) }))}
            />
          </div>
          <div>
            <label htmlFor={`${ch.id}-tracking`} style={LABEL_S}>{funnelText(content, "trafficTrackingSetupLabel", "Tracking setup")}</label>
            <CustomSelect
              id={`${ch.id}-tracking`}
              value={ch.trackingSetup ?? "utm_only"}
              onChange={(v) => onChange({ ...ch, trackingSetup: v as TrafficChannel["trackingSetup"] })}
              options={TRACKING_SETUP_OPTIONS.map((option) => ({ value: option.value, label: funnelText(content, option.key, option.fallback) }))}
            />
          </div>
          <div>
            <label htmlFor={`${ch.id}-temperature`} style={LABEL_S}>{funnelText(content, "trafficAudienceTemperatureLabel", "Audience temperature")}</label>
            <CustomSelect
              id={`${ch.id}-temperature`}
              value={ch.audienceTemperature ?? ch.intentLevel ?? "cold"}
              onChange={(v) => onChange({ ...ch, audienceTemperature: v as TrafficChannel["audienceTemperature"] })}
              options={AUDIENCE_TEMPERATURE_OPTIONS.map((option) => ({ value: option.value, label: funnelText(content, option.key, option.fallback) }))}
            />
          </div>
          <div>
            <label htmlFor={`${ch.id}-device`} style={LABEL_S}>{funnelText(content, "trafficDeviceLabel", "Device")}</label>
            <CustomSelect
              id={`${ch.id}-device`}
              value={ch.device ?? "all"}
              onChange={(v) => onChange({ ...ch, device: v as TrafficChannel["device"] })}
              options={DEVICE_OPTIONS.map((option) => ({ value: option.value, label: funnelText(content, option.key, option.fallback) }))}
            />
          </div>
          <div>
            <label htmlFor={`${ch.id}-geo`} style={LABEL_S}>{funnelText(content, "trafficGeoLabel", "Geo / market")}</label>
            <input
              id={`${ch.id}-geo`}
              type="text"
              maxLength={80}
              value={ch.geo ?? ""}
              placeholder={funnelText(content, "trafficGeoPlaceholder", "e.g. US, DACH, Tel Aviv")}
              onChange={(e) => onChange({ ...ch, geo: e.target.value || undefined })}
              {...INPUT_FOCUS_HANDLERS}
              style={INPUT_S}
            />
          </div>
          <div>
            <label htmlFor={`${ch.id}-budget-cap`} style={LABEL_S}>{funnelText(content, "trafficBudgetCapLabel", "Budget cap")}</label>
            <input
              id={`${ch.id}-budget-cap`}
              type="number"
              min={0}
              value={ch.monthlyBudgetCap ?? ""}
              onChange={(e) => onChange({ ...ch, monthlyBudgetCap: parseOptionalFloat(e.target.value) })}
              {...INPUT_FOCUS_HANDLERS}
              style={INPUT_S}
            />
          </div>
          <div>
            <label htmlFor={`${ch.id}-ramp`} style={LABEL_S}>{funnelText(content, "trafficRampTypeLabel", "Ramp type")}</label>
            <CustomSelect
              id={`${ch.id}-ramp`}
              value={ch.rampType ?? "flat"}
              onChange={(v) => onChange({ ...ch, rampType: v as TrafficChannel["rampType"] })}
              options={RAMP_TYPE_OPTIONS.map((option) => ({ value: option.value, label: funnelText(content, option.key, option.fallback) }))}
            />
          </div>
          <div>
            <label htmlFor={`${ch.id}-confidence`} style={LABEL_S}>{funnelText(content, "trafficConfidenceLabel", "Confidence")}</label>
            <CustomSelect
              id={`${ch.id}-confidence`}
              value={ch.confidence ?? "medium"}
              onChange={(v) => onChange({ ...ch, confidence: v as TrafficChannel["confidence"] })}
              options={CONFIDENCE_OPTIONS.map((option) => ({ value: option.value, label: funnelText(content, option.key, option.fallback) }))}
            />
          </div>
        </div>
      </details>
    </div>
  );
}
